import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Home, 
  BarChart, 
  FileText, 
  ShoppingCart, 
  Settings, 
  Rocket
} from 'lucide-react';
import { SidebarProps } from '../../types/layout.types';
import { useApp } from '../../context/AppContext';

export default function Sidebar({ isExpanded, onToggle }: SidebarProps) {
  const { currentUser } = useApp();
  const location = useLocation();
  const currentPath = location.pathname;

  // Track expanded menu sections
  const [isOrdersOpen, setIsOrdersOpen] = useState<boolean>(() => {
    return currentPath.startsWith('/orders') || currentPath === '/';
  });
  const [isProductsOpen, setIsProductsOpen] = useState<boolean>(() => {
    return currentPath.startsWith('/products');
  });

  // Since we want manual click toggling, we do not force auto-expand on every route update anymore, 
  // allowing the user to open/close menu items manually. We've removed path tracking useEffect to achieve exact print behavior.

  // Automatic Sidebar collapse helper for tablets/smaller screens under 1200px width limit
  const handleItemClick = () => {
    if (window.innerWidth < 1200 && isExpanded) {
      onToggle();
    }
  };

  // Active state conditions
  const isHomeActive = currentPath === '/home';
  const isReportsActive = currentPath === '/reports';
  const isOrdersActive = currentPath.startsWith('/orders') || currentPath === '/';
  const isProductsActive = currentPath.startsWith('/products');

  // Custom theme colors matching screen exactly
  const sidebarBg = '#333333';          // Flat dark graphite from print
  const submenuBg = '#1e1e1e';          // Near-black contrast wrapper
  const activeBlueBg = '#337ab7';       // High-contrast blue highlighted link
  const borderLineColor = '#4e4e4e';    // Thin high-contrast helper divider

  // Custom inline components for the play bar / split arrow button |>
  const ExpandIcon = () => (
    <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '22px', height: '22px' }}>
      <div style={{ width: '4px', height: '14px', backgroundColor: '#FFFFFF', marginRight: '3px' }} />
      <div style={{
        width: 0,
        height: 0,
        borderTop: '7px solid transparent',
        borderBottom: '7px solid transparent',
        borderLeft: '11px solid #FFFFFF'
      }} />
    </div>
  );

  const CollapseIcon = () => (
    <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '22px', height: '22px' }}>
      <div style={{
        width: 0,
        height: 0,
        borderTop: '7px solid transparent',
        borderBottom: '7px solid transparent',
        borderRight: '11px solid #FFFFFF',
        marginRight: '3px'
      }} />
      <div style={{ width: '4px', height: '14px', backgroundColor: '#FFFFFF' }} />
    </div>
  );

  const [mobilePopover, setMobilePopover] = useState<'orders' | 'products' | null>(null);

  return (
    <>
      {/* 1. OVERLAY DE BACKDROP TRANSPARENTE PARA FECHAR POPUPS MOBILE */}
      {mobilePopover && (
        <div 
          className="position-fixed w-100 h-100 top-0 start-0 d-md-none" 
          style={{ zIndex: 1040, backgroundColor: 'transparent' }} 
          onClick={() => setMobilePopover(null)} 
        />
      )}

      {/* 2. SIDEBAR LATERAL PARA GRANDES TELAS (PC, TABLETS EM MODO PAISAGEM) */}
      <aside 
        id="sidebar-container"
        className="text-white d-none d-md-flex flex-column h-100 flex-shrink-0"
        style={{ 
          width: isExpanded ? '260px' : '75px', 
          backgroundColor: sidebarBg,
          transition: 'width 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          borderRight: `1px solid ${borderLineColor}`
        }}
      >
        
        {/* TOP & MAIN NAVIGATION (SCROLLABLE AREA) */}
        <div className="flex-grow-1 overflow-y-auto" id="sidebar-top-nav" style={{ overflowX: 'hidden' }}>
          <div className="d-flex flex-column" style={{ padding: '0px' }}>
            
            {/* Main Item: Início */}
            <NavLink 
              id="nav-home"
              to="/home" 
              onClick={handleItemClick}
              className="d-flex align-items-center gap-3 px-3 py-3 text-white text-decoration-none border-bottom border-secondary border-opacity-10"
              style={({ isActive }) => ({
                backgroundColor: isActive ? activeBlueBg : 'transparent',
                fontSize: '15px',
                fontWeight: 600
              })}
            >
              <Home size={22} className="flex-shrink-0 text-white" />
              {isExpanded && <span className="font-sans text-white">Início</span>}
            </NavLink>

            {/* Main Item: Relatórios */}
            {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.relatorios === true) && (
              <NavLink 
                id="nav-reports"
                to="/reports" 
                onClick={handleItemClick}
                className="d-flex align-items-center gap-3 px-3 py-3 text-white text-decoration-none border-bottom border-secondary border-opacity-10"
                style={({ isActive }) => ({
                  backgroundColor: isActive ? activeBlueBg : 'transparent',
                  fontSize: '15px',
                  fontWeight: 600
                })}
              >
                <BarChart size={22} className="flex-shrink-0 text-white" />
                {isExpanded && <span className="font-sans text-white">Relatórios</span>}
              </NavLink>
            )}

            {/* Main Item Group: Pedidos */}
            {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.pedido === true) && (
              <div className="d-flex flex-column">
                <NavLink 
                  id="nav-orders-main"
                  to="/orders" 
                  className="d-flex align-items-center gap-3 px-3 py-3 text-white text-decoration-none"
                  style={{
                    backgroundColor: isOrdersActive ? activeBlueBg : 'transparent',
                    fontSize: '15px',
                    fontWeight: 600
                  }}
                  onClick={(e) => {
                    if (!isExpanded) {
                      onToggle();
                      setIsOrdersOpen(true);
                    } else {
                      setIsOrdersOpen(!isOrdersOpen);
                    }
                  }}
                >
                  <FileText size={22} className="flex-shrink-0 text-white" />
                  {isExpanded && <span className="font-sans text-white">Pedidos</span>}
                </NavLink>

                {/* Sub-item child with guaranteed peak visibility (Black block, white text) */}
                {isOrdersOpen && isExpanded && (
                  <NavLink
                    id="nav-orders-sub-list"
                    to="/orders"
                    onClick={handleItemClick}
                    style={({ isActive }) => ({
                      backgroundColor: isActive ? '#1e1e1e' : submenuBg,
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: 500,
                      paddingLeft: '52px',
                      paddingTop: '10px',
                      paddingBottom: '10px'
                    })}
                    className="d-block text-decoration-none hover:bg-opacity-80 border-bottom border-secondary border-opacity-25"
                  >
                    Lista de pedidos
                  </NavLink>
                )}
              </div>
            )}

            {/* Main Item Group: Produtos */}
            {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.produto === true || currentUser?.permissions?.view?.categoria === true) && (
              <div className="d-flex flex-column">
                <NavLink 
                  id="nav-products-main"
                  to="/products/list" 
                  className="d-flex align-items-center gap-3 px-3 py-3 text-white text-decoration-none"
                  style={{
                    backgroundColor: isProductsActive ? activeBlueBg : 'transparent',
                    fontSize: '15px',
                    fontWeight: 600
                  }}
                  onClick={(e) => {
                    if (!isExpanded) {
                      onToggle();
                      setIsProductsOpen(true);
                    } else {
                      setIsProductsOpen(!isProductsOpen);
                    }
                  }}
                >
                  <ShoppingCart size={22} className="flex-shrink-0 text-white" />
                  {isExpanded && <span className="font-sans text-white">Produtos</span>}
                </NavLink>

                {/* Sub-item child list with high-contrast text */}
                {isProductsOpen && isExpanded && (
                  <div className="d-flex flex-column" style={{ backgroundColor: submenuBg }}>
                    {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.produto === true) && (
                      <NavLink
                        id="nav-products-sub-list"
                        to="/products/list"
                        onClick={handleItemClick}
                        style={({ isActive }) => ({
                          backgroundColor: isActive ? '#1e1e1e' : 'transparent',
                          color: '#ffffff',
                          fontSize: '14px',
                          fontWeight: 500,
                          paddingLeft: '52px',
                          paddingTop: '10px',
                          paddingBottom: '10px'
                        })}
                        className="d-block text-decoration-none border-bottom border-secondary border-opacity-25"
                      >
                        Lista de produtos
                      </NavLink>
                    )}
                    {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.categoria === true) && (
                      <NavLink
                        id="nav-products-sub-categories"
                        to="/products/categories"
                        onClick={handleItemClick}
                        style={({ isActive }) => ({
                          backgroundColor: isActive ? '#1e1e1e' : 'transparent',
                          color: '#ffffff',
                          fontSize: '14px',
                          fontWeight: 500,
                          paddingLeft: '52px',
                          paddingTop: '10px',
                          paddingBottom: '10px'
                        })}
                        className="d-block text-decoration-none border-bottom border-secondary border-opacity-25"
                      >
                        Categorias
                      </NavLink>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* SYSTEM BOUNDARY (ANCHORED AT FOOTER OF FLEXBOX) */}
        <div 
          className="d-flex flex-column align-self-stretch mt-auto id-sidebar-anchored-footer" 
          style={{ borderTop: `1px solid ${borderLineColor}` }}
        >
          {/* Item: Configurações with divider beneath */}
          {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.configuracao === true) && (
            <NavLink 
              id="nav-settings-p"
              to="/settings" 
              onClick={handleItemClick}
              className="d-flex align-items-center gap-3 px-3 py-3 text-white text-decoration-none"
              style={({ isActive }) => ({
                backgroundColor: isActive ? activeBlueBg : 'transparent',
                fontSize: '15px',
                fontWeight: 500,
                borderBottom: `1PX solid ${borderLineColor}`
              })}
            >
              <Settings size={22} className="flex-shrink-0 text-white" />
              {isExpanded && <span className="font-sans text-white">Configurações</span>}
            </NavLink>
          )}

          {/* Item: Ajuda with divider beneath */}
          <NavLink 
            id="nav-help-p"
            to="/help" 
            className="d-flex align-items-center gap-3 px-3 py-3 text-white text-decoration-none"
            style={({ isActive }) => ({
              backgroundColor: isActive ? activeBlueBg : 'transparent',
              fontSize: '15px',
              fontWeight: 500,
              borderBottom: `1PX solid ${borderLineColor}`
            })}
          >
            <Rocket size={22} className="flex-shrink-0 text-white" />
            {isExpanded && <span className="font-sans text-white">Ajuda</span>}
          </NavLink>

          {/* Item: Expandir / Recolher menu toggle trigger */}
          <button
            id="nav-toggle-trigger"
            onClick={onToggle}
            className="btn text-white w-100 d-flex align-items-center gap-3 px-3 py-3 border-0 rounded-0 text-start cursor-pointer"
            style={{ 
              backgroundColor: 'transparent',
              fontSize: '15px',
              fontWeight: 500
            }}
            type="button"
          >
            {isExpanded ? (
              <>
                <CollapseIcon />
                <span className="font-sans text-white">Recolher menu</span>
              </>
            ) : (
              <ExpandIcon />
            )}
          </button>
        </div>

      </aside>

      {/* 3. MULTI-OPTION POPUPS ELEGANTES PARA DISPOSITIVOS MÓVEIS */}
      <div className="d-md-none" style={{ position: 'relative', zIndex: 1050 }}>
        {/* Popover flutuante para Pedidos */}
        {mobilePopover === 'orders' && (currentUser?.role === 'ADM' || currentUser?.permissions?.view?.pedido === true) && (
          <div 
            className="position-fixed bg-dark border border-secondary border-opacity-50 p-2 rounded-3 shadow-lg text-center"
            style={{
              bottom: '75px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '180px',
              backgroundColor: '#1e1e1e'
            }}
          >
            <div className="text-xxs font-mono text-muted uppercase tracking-wider mb-2 border-bottom border-light border-opacity-10 pb-1">Menu Pedidos</div>
            <NavLink
              to="/orders"
              onClick={() => setMobilePopover(null)}
              className={({ isActive }) => `d-block text-decoration-none py-2 px-3 rounded text-xs text-start ${isActive ? 'text-white fw-bold' : 'text-white-50'}`}
              style={{ backgroundColor: currentPath === '/orders' ? activeBlueBg : 'transparent' }}
            >
              Lista de pedidos
            </NavLink>
          </div>
        )}

        {/* Popover flutuante para Produtos */}
        {mobilePopover === 'products' && (currentUser?.role === 'ADM' || currentUser?.permissions?.view?.produto === true || currentUser?.permissions?.view?.categoria === true) && (
          <div 
            className="position-fixed bg-dark border border-secondary border-opacity-50 p-2 rounded-3 shadow-lg text-center"
            style={{
              bottom: '75px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '180px',
              backgroundColor: '#1e1e1e'
            }}
          >
            <div className="text-xxs font-mono text-muted uppercase tracking-wider mb-2 border-bottom border-light border-opacity-10 pb-1">Menu Produtos</div>
            {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.produto === true) && (
              <NavLink
                to="/products/list"
                onClick={() => setMobilePopover(null)}
                className={({ isActive }) => `d-block text-decoration-none py-2 px-3 rounded text-xs text-start mb-1 ${isActive ? 'text-white fw-bold' : 'text-white-50'}`}
                style={{ backgroundColor: currentPath === '/products/list' ? activeBlueBg : 'transparent' }}
              >
                Lista de produtos
              </NavLink>
            )}
            {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.categoria === true) && (
              <NavLink
                to="/products/categories"
                onClick={() => setMobilePopover(null)}
                className={({ isActive }) => `d-block text-decoration-none py-2 px-3 rounded text-xs text-start ${isActive ? 'text-white fw-bold' : 'text-white-50'}`}
                style={{ backgroundColor: currentPath === '/products/categories' ? activeBlueBg : 'transparent' }}
              >
                Categorias
              </NavLink>
            )}
          </div>
        )}
      </div>

      {/* 4. BOTTOM NAVIGATION BAR (FIXED-BOTTOM) PARA DISPOSITIVOS MÓVEIS */}
      <nav 
        id="bottom-nav-mobile"
        className="d-flex d-md-none fixed-bottom justify-content-around align-items-center border-top border-secondary border-opacity-25"
        style={{
          height: '65px',
          zIndex: 1045,
          backgroundColor: '#262626',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.15)'
        }}
      >
        {/* Link: Início */}
        <NavLink 
          to="/home" 
          onClick={() => setMobilePopover(null)}
          className={({ isActive }) => `d-flex flex-column align-items-center justify-content-center text-decoration-none flex-grow-1 h-100 py-1 ${isActive ? 'text-warning' : 'text-white-50'}`}
          style={({ isActive }) => ({
            color: isActive ? activeBlueBg : '#b0b0b0',
            transition: 'all 0.15s ease'
          })}
        >
          <Home size={20} style={{ color: currentPath === '/home' ? activeBlueBg : '#b0b0b0' }} />
          <span className="font-mono mt-1 text-center font-bold" style={{ fontSize: '9px', color: currentPath === '/home' ? '#ffffff' : '#a0a0a0' }}>Início</span>
        </NavLink>

        {/* Link: Relatórios */}
        {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.relatorios === true) && (
          <NavLink 
            to="/reports" 
            onClick={() => setMobilePopover(null)}
            className={({ isActive }) => `d-flex flex-column align-items-center justify-content-center text-decoration-none flex-grow-1 h-100 py-1 ${isActive ? 'text-warning' : 'text-white-50'}`}
            style={({ isActive }) => ({
              color: isActive ? activeBlueBg : '#b0b0b0',
              transition: 'all 0.15s ease'
            })}
          >
            <BarChart size={20} style={{ color: currentPath === '/reports' ? activeBlueBg : '#b0b0b0' }} />
            <span className="font-mono mt-1 text-center font-bold" style={{ fontSize: '10px', color: currentPath === '/reports' ? '#ffffff' : '#a0a0a0' }}>BI</span>
          </NavLink>
        )}

        {/* Button: Pedidos (com menu popup) */}
        {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.pedido === true) && (
          <button 
            onClick={() => setMobilePopover(mobilePopover === 'orders' ? null : 'orders')}
            className="btn d-flex flex-column align-items-center justify-content-center flex-grow-1 h-100 py-1 border-0 rounded-0 bg-transparent text-decoration-none"
            style={{ transition: 'all 0.15s ease' }}
            type="button"
          >
            <FileText size={20} style={{ color: isOrdersActive ? activeBlueBg : '#b0b0b0' }} />
            <span className="font-mono mt-1 text-center font-bold text-white-50" style={{ fontSize: '10px', color: isOrdersActive ? '#ffffff' : '#a0a0a0' }}>Pedidos</span>
          </button>
        )}

        {/* Button: Produtos (com menu popup) */}
        {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.produto === true || currentUser?.permissions?.view?.categoria === true) && (
          <button 
            onClick={() => setMobilePopover(mobilePopover === 'products' ? null : 'products')}
            className="btn d-flex flex-column align-items-center justify-content-center flex-grow-1 h-100 py-1 border-0 rounded-0 bg-transparent text-decoration-none"
            style={{ transition: 'all 0.15s ease' }}
            type="button"
          >
            <ShoppingCart size={20} style={{ color: isProductsActive ? activeBlueBg : '#b0b0b0' }} />
            <span className="font-mono mt-1 text-center font-bold text-white-50" style={{ fontSize: '10px', color: isProductsActive ? '#ffffff' : '#a0a0a0' }}>Produtos</span>
          </button>
        )}

        {/* Link: Configurações */}
        {(currentUser?.role === 'ADM' || currentUser?.permissions?.view?.configuracao === true) && (
          <NavLink 
            to="/settings" 
            onClick={() => setMobilePopover(null)}
            className={({ isActive }) => `d-flex flex-column align-items-center justify-content-center text-decoration-none flex-grow-1 h-100 py-1 ${isActive ? 'text-warning' : 'text-white-50'}`}
            style={({ isActive }) => ({
              color: isActive ? activeBlueBg : '#b0b0b0',
              transition: 'all 0.15s ease'
            })}
          >
            <Settings size={20} style={{ color: currentPath === '/settings' ? activeBlueBg : '#b0b0b0' }} />
            <span className="font-mono mt-1 text-center font-bold" style={{ fontSize: '10px', color: currentPath === '/settings' ? '#ffffff' : '#a0a0a0' }}>Ajustes</span>
          </NavLink>
        )}
      </nav>
    </>
  );
}
